import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import alert from 'react-hot-toast';

import _compact from 'lodash/compact';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isString from 'lodash/isString';
import _omit from 'lodash/omit';
import _trim from 'lodash/trim';

import SegmentPicker from './SegmentPicker';
import SendProgressDialog from './SendProgressDialog';
import SendTestDialog from './SendTestDialog';
import Targeting from './Targeting';
import { withContext } from './NotificationContext';
import i18n from './messages.json';
import { brazeService, messageService } from '../../services';
import { buildCatalog, resolveTargetCampaignId } from '../../services/campaign-catalog-service';
import { CONSTANTS } from '../../utils/common';
import { validURL } from '../../utils/url-helper';

import ArticleOrUrlField from './compose/ArticleOrUrlField';
import CampaignTriggerField from './compose/CampaignTriggerField';
import ComposeFooter from './compose/ComposeFooter';
import CustomImageField from './compose/CustomImageField';
import LinkTypeField from './compose/LinkTypeField';
import PreviewView from './compose/PreviewView';
import ScheduleConflictDialog from './compose/ScheduleConflictDialog';
import ScheduleDialog from './compose/ScheduleDialog';
import SoftLimitField from './compose/SoftLimitField';
import {
  zonedWallTimeToUtcIso,
  utcIsoToZonedWallTime,
  buildScheduleIds,
  findScheduledConflicts,
  formatScheduledTime,
} from '../../utils/schedule-helper';

import './Notification.css';

const ArticleType = 'Article';
const DirectUrlType = 'DirectURL';

const TITLE_SOFT_LIMIT = 30;
const BODY_SOFT_LIMIT = 150;

// Turn a failed Braze test-send into a short, human reason for the results row.
// An email the backend can't resolve to a Braze user comes back as a 404 (new
// API) or a schema 400 (older API) — either way show a concise line instead of a
// raw "Required" / opaque backend string. Non-email failures surface the backend
// message (falling back to a generic line).
const resolveBrazeTestError = (err, recipient) => {
  const status = _get(err, 'status');

  if (recipient.includes('@') && (status === 400 || status === 404)) {
    return 'No Braze user found for this email';
  }

  return _get(err, 'error') || _get(err, 'message') || 'Send failed';
};

const preprocessTopicTargeting = (targeting) => {
  const targetingType = _get(targeting, 'targetType.label', '');

  if (targetingType !== 'Topic') return targeting;

  const topics = targeting.selected;

  if (Array.isArray(topics) && topics.length > 0 && _isString(topics[0])) {
    return targeting;
  }

  const traverseTopicTree = (nodes, acc = []) => {
    nodes.forEach((topic) => {
      const { key, selected, children } = topic;

      if (selected) acc.push(key);
      traverseTopicTree(children, acc);
    });
    return acc;
  };

  targeting.selected = traverseTopicTree(topics);
};

const hasEffectiveImageUrl = (currentMessage) => {
  const custom = _trim(_get(currentMessage, 'imageUrl.value', ''));

  if (custom && validURL(custom)) return true;

  return _isEmpty(_get(currentMessage, 'article.image_url')) === false;
};

const isRenderRichPushNotification = (currentMessage, isIncludeImage) => (
  hasEffectiveImageUrl(currentMessage) && isIncludeImage
);

const NotificationForm = (props) => {
  const { context, onSave, onDone, onHide, show } = props;
  const { currentMessage, campaigns, campaignSizes = {}, isCampaignsLoading, rootTopic } = context;

  // Classify the Braze campaigns into atomic segments + combinations; used to
  // resolve the selected segments to the single campaign we send/schedule to.
  const catalog = useMemo(() => buildCatalog(campaigns || []), [campaigns]);

  const [isRenderPreview, setIsRenderPreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [typeNotification, setTypeNotification] = useState(ArticleType);
  const [showCampaignPicker, setShowCampaignPicker] = useState(false);
  const [showSendTest, setShowSendTest] = useState(false);
  const [sendLater, setSendLater] = useState(false);
  const [scheduleTime, setScheduleTime] = useState(null);
  const [isScheduling, setIsScheduling] = useState(false);
  const [scheduleConflicts, setScheduleConflicts] = useState(null);
  // Live progress for the Send Now flow (null when not sending).
  const [sendProgress, setSendProgress] = useState(null);
  // "Send Later" datetime popup (opened from the preview footer).
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);

  const directUrlValue = _get(currentMessage, 'directUrl.value', '');
  const messageId = _get(currentMessage, 'id', null);

  // Remembers what the user typed in the *inactive* link input so toggling the
  // Article ↔ Direct URL radio back and forth within a session doesn't wipe it.
  // `currentMessage` still only ever holds the active variant (so save / send /
  // reload logic is unchanged) — this ref holds the other one on the side.
  const linkStashRef = useRef({ article: null, directUrl: '' });

  // Preload the timing controls when editing an existing scheduled push.
  const editedScheduledSendTime = _get(currentMessage, 'scheduledSendTime', null);

  useEffect(() => {
    if (editedScheduledSendTime) {
      setSendLater(true);
      // Show the Eastern wall-clock the push was scheduled for, not the browser's.
      setScheduleTime(utcIsoToZonedWallTime(editedScheduledSendTime));
    }
  }, [editedScheduledSendTime]);

  // Sync the link-type radio to the message being composed/edited. Keyed on the
  // message id (not directUrlValue) so loading a *different* message resets the
  // type both ways — otherwise the local typeNotification leaks across edits:
  // compose a Direct URL push, then open Edit on an Article draft, and it stays
  // stuck on Direct URL, hiding the article link until the modal is reopened
  // (WWW-15517 QA #3). Not keyed on directUrlValue so toggling the radio to
  // Direct URL (which starts empty) isn't immediately reverted to Article.
  useEffect(() => {
    setTypeNotification(_isEmpty(directUrlValue) ? ArticleType : DirectUrlType);
    // A different message is now loaded — reset the toggle stash to its link
    // fields so a stashed value from the previous message can't bleed in.
    linkStashRef.current = {
      article: _get(currentMessage, 'article', null),
      directUrl: directUrlValue || '',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId]);

  const updateCurrentMessage = useCallback((patch) => {
    const { updateState } = context;
    Object.assign(currentMessage, patch);
    updateState({ currentMessage });
  }, [context, currentMessage]);

  const handleTitleChanged = useCallback((event) => {
    const { value } = event.target;
    const titleMessages = i18n.ink.notification.title;
    const trimedValue = _trim(value);

    const isValid = trimedValue.length > 0;
    const error = isValid ? '' : titleMessages.required;

    updateCurrentMessage({ title: { value, isValid, error } });
  }, [updateCurrentMessage]);

  const handleBodyChanged = useCallback((event) => {
    const { value } = event.target;
    const { body } = i18n.ink.notification;
    const trimedValue = _trim(value);

    const isValid = trimedValue.length > 0;
    const error = isValid ? '' : body.required;

    updateCurrentMessage({ body: { value, isValid, error } });
  }, [updateCurrentMessage]);

  const handleIsIncludeImageChange = useCallback((event) => {
    updateCurrentMessage({ isIncludeImage: event.target.checked });
  }, [updateCurrentMessage]);

  const handleImageUrlChange = useCallback((event) => {
    updateCurrentMessage({ imageUrl: { value: event.target.value } });
  }, [updateCurrentMessage]);

  const handleArticleChanged = useCallback((article) => {
    updateCurrentMessage({ article });
  }, [updateCurrentMessage]);

  const getEffectiveImageUrl = useCallback(() => {
    const custom = _trim(_get(currentMessage, 'imageUrl.value', ''));

    if (custom && validURL(custom)) return custom;

    return _get(currentMessage, 'article.image_url', '');
  }, [currentMessage]);

  const thumbnailSrc = getEffectiveImageUrl();

  const buildSaveData = useCallback((statusOverride) => {
    const customizedTargeting = Object.assign({}, currentMessage.targeting);
    preprocessTopicTargeting(customizedTargeting);

    const effectiveImageUrl = getEffectiveImageUrl();
    const baseArticle = currentMessage.article;
    let articleForSave = baseArticle;

    if (baseArticle && effectiveImageUrl && effectiveImageUrl !== _get(baseArticle, 'image_url')) {
      articleForSave = { ...baseArticle, image_url: effectiveImageUrl };
    } else if (!baseArticle && effectiveImageUrl) {
      articleForSave = { image_url: effectiveImageUrl };
    }

    return {
      title: currentMessage.title.value,
      body: _trim(currentMessage.body.value),
      expandTitle: '',
      expandBody: '',
      article: articleForSave,
      isIncludeImage: _isEmpty(effectiveImageUrl)
        ? false
        : currentMessage.isIncludeImage,
      targeting: customizedTargeting,
      directUrl: currentMessage.directUrl.value,
      campaignIds: currentMessage.campaignIds || [],
      status: statusOverride || 'draft',
      // Cleared by default; handleSchedule overrides these when scheduling, and a
      // Save Draft on a previously-scheduled push clears them back out.
      scheduledSendTime: null,
      brazeScheduleIds: null,
    };
  }, [currentMessage, getEffectiveImageUrl]);

  const handleSave = useCallback(async (showSuccessAlert = true) => {
    if (!onSave) return undefined;

    // If this push currently has a live Braze schedule (editing a scheduled push
    // → Save Draft, or → Send Now), cancel it first — otherwise Braze would still
    // fire it (orphaned / duplicate send). Abort if we can't cancel, so we never
    // leave a draft/sent message with a live schedule still pending.
    const liveScheduleIds = (_get(currentMessage, 'brazeScheduleIds', []) || [])
      .filter((entry) => entry && entry.scheduleId);

    if (liveScheduleIds.length > 0) {
      const cancelled = await Promise.allSettled(
        liveScheduleIds.map((entry) =>
          brazeService.deleteScheduledCampaign({ campaign_id: entry.campaignId, schedule_id: entry.scheduleId })
        )
      );

      if (cancelled.some((r) => r.status === 'rejected')) {
        alert.error(i18n.ink.notification.scheduled.cancelFailure);

        return undefined;
      }
    }

    setIsRenderPreview(false);

    const data = buildSaveData('draft');
    // A draft remembers the picked "Send later" time (WWW-15236 #1) but has no
    // live Braze schedule of its own.
    data.scheduledSendTime = sendLater ? zonedWallTimeToUtcIso(scheduleTime) : null;
    data.brazeScheduleIds = null;

    const saved = await onSave(data, showSuccessAlert);

    // Move the item between lists right away (Scheduled Pushes ↔ Activity Log)
    // instead of waiting for a manual page refresh.
    await context.getScheduledMessages?.();
    await context.getMessages?.(1, false);

    return saved;
  }, [onSave, buildSaveData, currentMessage, sendLater, scheduleTime, context]);

  const buildBrazeBasePayload = useCallback((msg) => {
    const article = _get(currentMessage, 'article');

    const deepLink = typeNotification === DirectUrlType
      ? msg.directUrl
      : _get(article, 'story_url', '');

    const imageUrl = currentMessage.isIncludeImage
      ? getEffectiveImageUrl()
      : '';

    const arcId = _get(article, 'story_id', '');

    const payload = { body: msg.body };

    payload.is_direct_url = typeNotification === DirectUrlType && Boolean(msg.directUrl);

    if (msg.title && msg.title.trim()) payload.title = msg.title;
    if (deepLink) {
      payload.deep_link = deepLink;
      payload.article_link = deepLink;
    }
    if (imageUrl) payload.image_url = imageUrl;
    if (arcId) payload.arc_id = arcId;

    return payload;
  }, [currentMessage, typeNotification, getEffectiveImageUrl]);

  const handleSend = useCallback(async () => {
    document.getElementsByClassName('_modal-form_')[0].style.display = 'none';

    const msg = await handleSave(false);

    // handleSave aborts (returns undefined) when a live Braze schedule couldn't be
    // cancelled — don't send in that case; restore the form so the user can retry.
    if (!msg) {
      document.getElementsByClassName('_modal-form_')[0].style.display = '';

      return;
    }

    const targetingType = _get(msg, 'targeting.targetType.label', '');

    if (targetingType === 'Topic') {
      const reduceTopicSelected = (topics, acc = [], level = 1) => {
        topics.forEach((topic) => {
          const { key, selected, children } = topic;

          if (selected) acc.push(key);
          else if (level <= CONSTANTS.topic_hierarchy_max_level) {
            reduceTopicSelected(children, acc, level + 1);
          }
        });
        return acc;
      };

      msg.targeting.selected = reduceTopicSelected(rootTopic.children);
    }

    const baseBrazePayload = { ...buildBrazeBasePayload(msg), broadcast: true };
    const campaignIds = _get(currentMessage, 'campaignIds', []) || [];
    const campaignNameById = new Map(campaigns.map((c) => [c.id, c.name]));
    const names = campaignIds.map((id) => campaignNameById.get(id) || id);
    // The selected atomic segments resolve to ONE Braze campaign (atomic when
    // one is picked, the pre-built combined campaign when several are) — we fire
    // a single trigger to it. The segment steps are informational and all settle
    // together off that one send.
    const targetId = resolveTargetCampaignId(catalog, campaignIds);

    // Open the live progress modal. The selected segments resolve to ONE Braze
    // send, so show a single Braze row (the resolved campaign — the combined one
    // when several segments were picked) alongside the app-push step.
    const targetName = (targetId && catalog.nameById.get(targetId)) || names.join(' | ') || 'Braze push';

    setSendProgress({
      phase: 'sending',
      fcm: 'sending',
      segments: [{ id: targetId || 'braze', name: targetName, status: 'sending' }],
    });

    const setAllSegments = (status) =>
      setSendProgress((p) => (p ? { ...p, segments: p.segments.map((s) => ({ ...s, status })) } : p));

    // App push (FCM) — drives the activity-log row status.
    let fcmResponse;

    try {
      fcmResponse = await messageService.sendMessage(_omit(msg, ['campaignIds']));
      const fcmFailed = fcmResponse?.responses && fcmResponse.failureCount !== 0;

      setSendProgress((p) => (p ? { ...p, fcm: fcmFailed ? 'failed' : 'sent' } : p));
    } catch (e) {
      fcmResponse = _get(e, 'error', e);
      setSendProgress((p) => (p ? { ...p, fcm: 'failed' } : p));
    }

    // Single Braze send to the resolved (atomic or combined) campaign.
    let brazeResults;

    if (!targetId) {
      setAllSegments('failed');
      brazeResults = { campaignIds, names, dispatchId: null, status: 'failed' };
    } else {
      try {
        const res = await brazeService.triggerCampaign({ ...baseBrazePayload, campaign_id: targetId });

        setAllSegments('sent');
        brazeResults = { campaignIds, names, dispatchId: res?.dispatch_id ?? null, status: 'sent' };
      } catch (e) {
        setAllSegments('failed');
        brazeResults = { campaignIds, names, dispatchId: null, status: 'failed' };
      }
    }

    msg.brazeResults = brazeResults;

    if (msg.id) {
      try {
        await messageService.updateMessage(msg.id, { id: msg.id, brazeResults });
      } catch (e) {
        console.error('Failed to persist Braze results', e);
      }
    }

    onDone(msg, fcmResponse);

    // A Send-Now'd scheduled push has been cancelled + delivered — refresh so it
    // leaves the Scheduled Pushes list and shows as delivered in the Activity Log
    // without a manual refresh (WWW-15236 #4).
    await context.getScheduledMessages?.();
    await context.getMessages?.(1, false);

    setSendProgress((p) => (p ? { ...p, phase: 'done' } : p));
  }, [handleSave, buildBrazeBasePayload, currentMessage, campaigns, catalog, rootTopic, onDone, context]);

  const handleDoneSend = useCallback(() => setSendProgress(null), []);

  // WWW-15236 — schedule the push for future delivery via Braze-native
  // scheduling instead of sending now. One schedule per selected campaign; the
  // returned schedule ids are persisted on the message (status `scheduled`) so it
  // shows in the Scheduled Pushes list and can be rescheduled / cancelled. No FCM
  // send happens at schedule time — Braze delivers the campaigns.
  const doSchedule = useCallback(async () => {
    // Interpret the picked wall-clock as Eastern (EDT/EST per date) → absolute UTC.
    const scheduleIso = zonedWallTimeToUtcIso(scheduleTime);

    if (!scheduleIso) return;

    const scheduleData = buildSaveData('scheduled');
    const baseBrazePayload = buildBrazeBasePayload(scheduleData);
    const campaignIds = _get(currentMessage, 'campaignIds', []) || [];
    // The selected atomic segments resolve to the ONE campaign we schedule.
    const targetId = resolveTargetCampaignId(catalog, campaignIds);
    // When rescheduling/editing an existing scheduled push, the old Braze
    // schedules must be cancelled first — otherwise we'd create brand-new
    // schedules alongside the originals (duplicate sends), while only persisting
    // the new ids. Campaigns may have changed too, so delete-then-recreate keeps
    // Braze and the persisted record in sync.
    const existingScheduleIds = (_get(currentMessage, 'brazeScheduleIds', []) || [])
      .filter((entry) => entry && entry.scheduleId);

    if (!targetId) {
      alert.error(i18n.ink.notification.scheduled.scheduleFailure);

      return;
    }

    setIsScheduling(true);

    try {
      if (existingScheduleIds.length > 0) {
        const cancelled = await Promise.allSettled(
          existingScheduleIds.map((entry) =>
            brazeService.deleteScheduledCampaign({
              campaign_id: entry.campaignId,
              schedule_id: entry.scheduleId,
            })
          )
        );

        // Safe recreate: only create the new schedule once the old one is gone.
        // If we can't cancel it, abort — never leave old + new both live (that
        // would double-send). The item stays scheduled and the user can retry.
        if (cancelled.some((r) => r.status === 'rejected')) {
          console.error('Failed to cancel prior Braze schedule during reschedule');
          alert.error(i18n.ink.notification.scheduled.scheduleFailure);

          return;
        }
      }

      // One schedule against the resolved (atomic or combined) campaign.
      const results = await Promise.allSettled([
        brazeService.scheduleCampaign({
          ...baseBrazePayload,
          broadcast: true,
          campaign_id: targetId,
          schedule_time: scheduleIso,
        }),
      ]);

      const brazeScheduleIds = buildScheduleIds([targetId], results);

      if (!brazeScheduleIds.some((entry) => entry.scheduleId)) {
        alert.error(i18n.ink.notification.scheduled.scheduleFailure);

        return;
      }

      // Snapshot the selected segment names + the Braze schedule id onto the
      // record so the activity log shows which segments the push went to. A
      // scheduled push has a schedule_id, not a dispatch_id (that only exists
      // once Braze delivers).
      const campaignNameById = new Map(campaigns.map((c) => [c.id, c.name]));
      const brazeResults = {
        campaignIds,
        names: campaignIds.map((id) => campaignNameById.get(id) || id),
        scheduleId: _get(brazeScheduleIds, '0.scheduleId', null),
        status: 'scheduled',
      };

      // Carry the record id so editing/rescheduling an existing scheduled push
      // UPDATES it instead of creating a duplicate (WWW-15236 #2/#6). Undefined
      // for a brand-new push → saveScheduledMessage creates it.
      const persisted = {
        ...scheduleData,
        id: _get(currentMessage, 'id'),
        scheduledSendTime: scheduleIso,
        brazeScheduleIds,
        brazeResults,
      };

      await context.saveScheduledMessage(persisted);
      // Refresh the Activity Log too, so a push scheduled *from a draft* leaves it
      // (WWW-15236 #3).
      await context.getMessages?.(1, false);

      alert.success(i18n.ink.notification.scheduled.scheduleSuccess);
      // Close the schedule popup + compose modal only once the schedule succeeds
      // (the popup showed "Scheduling…" until now). The Scheduled Pushes list was
      // refreshed by saveScheduledMessage. Deliberately NOT onDone() — that path
      // forces the message into the Activity Log as delivered, which a scheduled
      // push isn't.
      setShowScheduleDialog(false);
      setIsRenderPreview(false);
      onHide();
    } catch (e) {
      console.error('Failed to schedule push', e);
      alert.error(i18n.ink.notification.scheduled.scheduleFailure);
    } finally {
      setIsScheduling(false);
    }
  }, [scheduleTime, buildSaveData, buildBrazeBasePayload, currentMessage, campaigns, catalog, context, onHide]);

  // Schedule button: if a push is already scheduled to the SAME segments at the
  // SAME time, warn first (a soft guard, not a block) — otherwise schedule. Skips
  // the record being edited so a plain reschedule isn't flagged against itself.
  const handleSchedule = useCallback(() => {
    const scheduleIso = zonedWallTimeToUtcIso(scheduleTime);

    if (!scheduleIso) return;

    const conflicts = findScheduledConflicts(
      { campaignIds: currentMessage.campaignIds, scheduledSendTime: scheduleIso },
      context.scheduledMessages,
      _get(currentMessage, 'id'),
    );

    if (conflicts.length > 0) {
      // Hand off to the conflict dialog — close the schedule popup so the two
      // modals don't stack.
      setShowScheduleDialog(false);
      setScheduleConflicts(conflicts);

      return;
    }

    doSchedule();
  }, [scheduleTime, currentMessage, context, doSchedule]);

  const confirmScheduleAnyway = useCallback(() => {
    setScheduleConflicts(null);
    doSchedule();
  }, [doSchedule]);

  // "Go back" from the conflict warning → return to the time picker (not the
  // preview) so the editor can pick a different slot.
  const cancelScheduleConflict = useCallback(() => {
    setScheduleConflicts(null);
    setShowScheduleDialog(true);
  }, []);

  // Existing colliding pushes shaped for the warning dialog (campaign ids → names).
  const conflictDetails = useMemo(() => {
    if (!scheduleConflicts) return [];

    const nameById = new Map(campaigns.map((c) => [c.id, c.name]));

    return scheduleConflicts.map((m) => ({
      id: m.id,
      title: m.title,
      body: m.body,
      link: m.directUrl || _get(m, 'article.story_url', '') || '',
      segments: (m.campaignIds || []).map((id) => nameById.get(id) || id).join(', '),
      time: formatScheduledTime(m.scheduledSendTime),
    }));
  }, [scheduleConflicts, campaigns]);

  const handleSendTest = useCallback(async ({ braze }) => {
    const baseData = buildSaveData('draft');

    let brazeResults = null;

    // WWW-15543 — a test push is delivered to the entered recipients via an ad-hoc
    // Braze /messages/send (built from the composed notification), NOT a campaign
    // trigger. That bypasses the campaign's target segment, so a tester receives
    // the push regardless of segment membership. A recipient may be a Braze User
    // ID (external_user_id) OR an email — /messages/send only targets User IDs, so
    // the lambda resolves an email to its external_id(s) via /users/export/ids
    // first. We auto-detect on '@' and send it in the matching field. One call per
    // recipient preserves the per-recipient ✓/✗ result list; an email that matches
    // no Braze user comes back as a 404 and renders as that recipient's ✗ row.
    if (braze && braze.recipients.length > 0) {
      const baseBrazePayload = buildBrazeBasePayload(baseData);

      const calls = braze.recipients.map((recipient) => {
        const identityField = recipient.includes('@')
          ? { emails: [recipient] }
          : { external_user_ids: [recipient] };

        return brazeService.sendTestMessage({ ...baseBrazePayload, ...identityField }).then(
          () => ({ ok: true, recipient }),
          (err) => ({
            ok: false,
            recipient,
            error: resolveBrazeTestError(err, recipient),
          }),
        );
      });

      brazeResults = await Promise.all(calls);
    }

    return { braze: brazeResults };
  }, [buildSaveData, buildBrazeBasePayload]);

  const handlePreview = useCallback(() => {
    // Use the same effective URL the inline thumbnail + FCM + Braze payload all use,
    // so the preview is WYSIWYG.
    if (_get(currentMessage, 'isIncludeImage', false)) {
      setPreviewImageUrl(getEffectiveImageUrl());
    }

    setIsRenderPreview(true);
  }, [currentMessage, getEffectiveImageUrl]);

  const handleNotificationTypeChange = useCallback((name) => {
    if (name === typeNotification) return;

    // Stash the value the user is leaving and restore whatever they had in the
    // input they're switching to, so flipping the radio back and forth doesn't
    // clear the article / direct URL they already entered. `currentMessage`
    // stays single-active (only the chosen variant populated) — the other value
    // lives in linkStashRef until they switch back.
    const stash = linkStashRef.current;
    const patch = {};

    if (name === DirectUrlType) {
      stash.article = _get(currentMessage, 'article', null);
      patch.article = null;
      patch.directUrl = { value: stash.directUrl || '' };
    } else {
      stash.directUrl = _get(currentMessage, 'directUrl.value', '');
      patch.directUrl = { value: '' };
      patch.article = stash.article || null;
    }

    setTypeNotification(name);
    updateCurrentMessage(patch);
  }, [typeNotification, currentMessage, updateCurrentMessage]);

  const handleDirectUrlChange = useCallback((event) => {
    updateCurrentMessage({ directUrl: { value: event.target.value } });
  }, [updateCurrentMessage]);

  const handleCampaignConfirm = useCallback((ids) => {
    updateCurrentMessage({ campaignIds: ids });
  }, [updateCurrentMessage]);

  const handleCampaignRemove = useCallback((id) => {
    const ids = currentMessage.campaignIds || [];

    // Removing the collapsed combined chip clears the whole combination;
    // otherwise drop the single atomic segment.
    if (!catalog.atomicById.has(id)) {
      updateCurrentMessage({ campaignIds: [] });

      return;
    }

    updateCurrentMessage({ campaignIds: ids.filter((x) => x !== id) });
  }, [currentMessage, catalog, updateCurrentMessage]);

  // Chips for the compose field / preview pills. A resolved 2+ combination
  // collapses to a single chip — the combined campaign (with its own size) —
  // instead of one chip per atomic segment; sizes come from the session map.
  const selectedCampaigns = useMemo(() => {
    const ids = currentMessage.campaignIds || [];

    if (ids.length === 0) return [];

    const targetId = resolveTargetCampaignId(catalog, ids);

    if (ids.length >= 2 && targetId && !catalog.atomicById.has(targetId)) {
      return [{ id: targetId, name: catalog.nameById.get(targetId) || targetId }];
    }

    return ids.map((id) => ({ id, name: catalog.nameById.get(id) || id }));
  }, [catalog, currentMessage.campaignIds]);

  const { title, body, targeting, directUrl, campaignIds, isIncludeImage, imageUrl } = currentMessage;

  const customImageUrlValue = _trim(_get(imageUrl, 'value', ''));
  const isCustomImageUrlValid = customImageUrlValue === '' || validURL(customImageUrlValue);

  const formIsValid = useMemo(() => {
    let isValidUrl = true;

    if (typeNotification === DirectUrlType) {
      isValidUrl = validURL(directUrl.value);
    }

    // The selected segments must resolve to exactly one Braze campaign (atomic,
    // or the pre-built combined campaign) — otherwise there's nothing to send to.
    const hasResolvableTarget = Boolean(resolveTargetCampaignId(catalog, campaignIds || []));

    return title.isValid && body.isValid && targeting.isValid && isValidUrl && hasResolvableTarget && isCustomImageUrlValid;
  }, [title, body, targeting, directUrl, campaignIds, typeNotification, isCustomImageUrlValid, catalog]);

  // "Send later" needs a valid, still-future time before the Schedule button enables.
  const scheduleTimeValid = useMemo(() => {
    if (!sendLater) return true;
    if (!scheduleTime) return false;

    // Compare the true absolute instant (Eastern wall-clock → UTC) against now,
    // so the future-check is correct regardless of the editor's browser timezone.
    const iso = zonedWallTimeToUtcIso(scheduleTime);
    const t = iso ? new Date(iso).getTime() : NaN;

    return !Number.isNaN(t) && t > Date.now();
  }, [sendLater, scheduleTime]);

  const isArticleValid =
    typeNotification !== ArticleType || _isEmpty(currentMessage.article) === false;

  // Preview-view derived props.
  const previewDeepLink = typeNotification === DirectUrlType
    ? directUrl.value
    : _get(currentMessage, 'article.story_url', '');
  const showPreviewImage = isRenderRichPushNotification(currentMessage, isIncludeImage);

  const openCampaignPicker = useCallback(() => setShowCampaignPicker(true), []);
  const closeCampaignPicker = useCallback(() => setShowCampaignPicker(false), []);
  const openSendTest = useCallback(() => setShowSendTest(true), []);
  const closeSendTest = useCallback(() => setShowSendTest(false), []);
  const exitPreview = useCallback(() => setIsRenderPreview(false), []);

  // Send Later → open the datetime popup. `sendLater` stays in sync so a
  // subsequent Save Draft remembers the picked time (WWW-15236 #1).
  const openScheduleDialog = useCallback(() => {
    setSendLater(true);
    setShowScheduleDialog(true);
  }, []);
  const closeScheduleDialog = useCallback(() => setShowScheduleDialog(false), []);

  // Confirm from the dialog: keep the dialog open (its Schedule button shows the
  // "Scheduling…" loading state) and run the conflict-checked schedule. The dialog
  // closes itself on success (doSchedule) or when a conflict needs surfacing
  // (handleSchedule) — so we never flash back to the preview mid-request.
  const handleScheduleConfirm = useCallback(() => {
    handleSchedule();
  }, [handleSchedule]);

  const handleCancel = useCallback(() => {
    setIsRenderPreview(false);
    onHide();
  }, [onHide]);

  const handleSaveClick = useCallback(() => handleSave(), [handleSave]);

  return (
    <>
      <Modal
        aria-labelledby='contained-modal-title-vcenter'
        centered
        backdrop={'static'}
        onHide={onHide}
        // Hide the compose/preview modal while the schedule popup, the
        // conflict warning, or an in-flight schedule is on screen — otherwise
        // closing the schedule popup to surface the conflict dialog would
        // re-reveal this modal behind it, stacking two popups (WWW-15517 QA #2).
        show={show && !showScheduleDialog && !scheduleConflicts && !isScheduling}
        className='_modal-form_'
        size={isRenderPreview ? 'lg' : 'xl'}
        dialogClassName={isRenderPreview ? 'notification-preview-dialog' : 'notification-compose-dialog'}
        backdropClassName='notification-compose-backdrop'
      >
        <Modal.Header closeButton>
          <Modal.Title id='contained-modal-title-vcenter'>
            {isRenderPreview
              ? i18n.ink.notification.dialog.previewTitle
              : i18n.ink.notification.dialog.title}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {isRenderPreview ? (
            <PreviewView
              selectedCampaigns={selectedCampaigns}
              title={title.value}
              body={body.value}
              typeNotification={typeNotification}
              deepLink={previewDeepLink}
              showImage={showPreviewImage}
              previewImageUrl={previewImageUrl}
            />
          ) : (
            <Form>
              <div className='notification-compose__grid'>
                <div className='notification-compose__column'>
                  <section className='notification-compose__section'>
                    <h6 className='notification-compose__section-title'>
                      {i18n.ink.notification.sections.link}
                    </h6>
                    <LinkTypeField
                      typeNotification={typeNotification}
                      onChange={handleNotificationTypeChange}
                    />
                    <ArticleOrUrlField
                      typeNotification={typeNotification}
                      article={currentMessage.article}
                      directUrl={directUrl.value}
                      isArticleValid={isArticleValid}
                      onArticleChange={handleArticleChanged}
                      onDirectUrlChange={handleDirectUrlChange}
                    />
                    <CustomImageField
                      value={_get(imageUrl, 'value', '')}
                      onUrlChange={handleImageUrlChange}
                      isCustomImageUrlValid={isCustomImageUrlValid}
                      thumbnailSrc={thumbnailSrc}
                      isIncludeImage={isIncludeImage}
                      onIncludeImageChange={handleIsIncludeImageChange}
                    />
                  </section>
                </div>

                <div className='notification-compose__column'>
                  <section className='notification-compose__section'>
                    <h6 className='notification-compose__section-title'>
                      {i18n.ink.notification.sections.audience}
                    </h6>
                    <CampaignTriggerField
                      selectedCampaigns={selectedCampaigns}
                      campaignIdsCount={(campaignIds || []).length}
                      isCampaignsLoading={isCampaignsLoading}
                      estimatesByCampaignId={campaignSizes}
                      isEstimateLoading={false}
                      onOpenPicker={openCampaignPicker}
                      onRemoveCampaign={handleCampaignRemove}
                    />
                    <Form.Group controlId='formTargeting' className='target-input'>
                      <Form.Label className='notification-compose__field-label'>
                        {i18n.ink.notification.targeting.label}
                      </Form.Label>
                      <Targeting />
                      <Form.Control isInvalid={!targeting.isValid} style={{ display: 'none' }} />
                      <Form.Control.Feedback type='invalid'>
                        {_compact(targeting.selected).length < CONSTANTS.max_token_num
                          ? i18n.ink.notification.targeting.required
                          : i18n.ink.notification.targeting.maxTokens}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </section>

                  <section className='notification-compose__section'>
                    <h6 className='notification-compose__section-title'>
                      {i18n.ink.notification.sections.message}
                    </h6>
                    <SoftLimitField
                      controlId='formTitle'
                      label={i18n.ink.notification.title.label}
                      placeholder={i18n.ink.notification.title.placeholder}
                      value={title.value}
                      isValid={title.isValid}
                      error={title.error}
                      onChange={handleTitleChanged}
                      softLimit={TITLE_SOFT_LIMIT}
                      softLimitWarning={i18n.ink.notification.title.softLimitWarning}
                    />
                    <SoftLimitField
                      controlId='formBody'
                      label={i18n.ink.notification.body.label}
                      placeholder={i18n.ink.notification.body.placeholder}
                      as='textarea'
                      rows='3'
                      value={body.value}
                      isValid={body.isValid}
                      error={body.error}
                      onChange={handleBodyChanged}
                      softLimit={BODY_SOFT_LIMIT}
                      softLimitWarning={i18n.ink.notification.body.softLimitWarning}
                    />
                  </section>
                </div>
              </div>
            </Form>
          )}
        </Modal.Body>

        <Modal.Footer className='notification-compose__footer'>
          <ComposeFooter
            isRenderPreview={isRenderPreview}
            isEditing={Boolean(currentMessage.id)}
            formIsValid={formIsValid}
            isArticleValid={isArticleValid}
            onCancel={handleCancel}
            onExitPreview={exitPreview}
            onSave={handleSaveClick}
            onSendTest={openSendTest}
            onPreview={handlePreview}
            onSendLater={openScheduleDialog}
            onSend={handleSend}
          />
        </Modal.Footer>
      </Modal>
      <SegmentPicker
        show={showCampaignPicker}
        onHide={closeCampaignPicker}
        onConfirm={handleCampaignConfirm}
        campaigns={campaigns}
        isLoading={isCampaignsLoading}
        selectedIds={currentMessage.campaignIds || []}
        sizes={campaignSizes}
      />
      <SendTestDialog
        show={showSendTest}
        onHide={closeSendTest}
        selectedCampaigns={selectedCampaigns}
        onSubmit={handleSendTest}
      />
      <SendProgressDialog progress={sendProgress} onDone={handleDoneSend} />
      <ScheduleDialog
        show={showScheduleDialog}
        scheduleTime={scheduleTime}
        onScheduleTimeChange={setScheduleTime}
        onCancel={closeScheduleDialog}
        onConfirm={handleScheduleConfirm}
        confirmDisabled={!scheduleTimeValid || isScheduling}
        isScheduling={isScheduling}
        preview={{
          selectedCampaigns,
          title: title.value,
          body: body.value,
          typeNotification,
          deepLink: previewDeepLink,
          showImage: showPreviewImage,
          previewImageUrl,
        }}
      />
      <ScheduleConflictDialog
        show={Boolean(scheduleConflicts)}
        conflicts={conflictDetails}
        isBusy={isScheduling}
        onConfirm={confirmScheduleAnyway}
        onCancel={cancelScheduleConflict}
      />
    </>
  );
};

export default withContext(NotificationForm);
